import type { UserRole } from '@/types'
import { PUBLIC_EXACT_PATHS, isKnownRoute } from './routeManifest'

export interface RouteAccessPolicy {
  requiresAuth: boolean
  roles?: UserRole[]
}

const BILLING_ROLES: UserRole[] = ['CHAMBER_CEO', 'MEMBERSHIP_MANAGER', 'IT_ADMIN']
const ADMIN_ROLES: UserRole[] = ['IT_ADMIN']

const ADMIN_PREFIXES = [
  '/settings',
  '/moderation',
  '/import',
  '/email-templates',
  '/webhooks',
  '/partner-portal',
]

const BILLING_PREFIXES = ['/billing']

function startsWithAny(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

function normalize(pathname: string): string {
  if (pathname.length <= 1) return pathname
  return pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname
}

export function resolveRouteAccessPolicy(pathname: string): RouteAccessPolicy {
  const path = normalize(pathname)

  // 1. Explicitly public routes.
  if (PUBLIC_EXACT_PATHS.has(path)) {
    return { requiresAuth: false }
  }

  // 2. Administrative surfaces require the IT admin role.
  if (startsWithAny(path, ADMIN_PREFIXES)) {
    return { requiresAuth: true, roles: ADMIN_ROLES }
  }

  // 3. Billing surfaces require a billing-capable role.
  if (startsWithAny(path, BILLING_PREFIXES)) {
    return { requiresAuth: true, roles: BILLING_ROLES }
  }

  // 4. Unknown URLs stay public so the NotFound page can render its noindex
  //    404 instead of redirecting anonymous visitors to signup.
  if (!isKnownRoute(path)) {
    return { requiresAuth: false }
  }

  // 5. Fail closed: any known route that is not explicitly public requires
  //    authentication. New routes are therefore protected by default, even if
  //    the author forgets to add an entry above.
  return { requiresAuth: true }
}

